
import React from 'react';
import { motion } from 'framer-motion';
import { CHAT_ANIMATIONS } from '@/utils/chat-animations';
import { cn } from '@/lib/utils';
import { OptimizedImage } from '@/components/ui/OptimizedImage/OptimizedImage';
import { CopilotAssistant } from '@/hooks/ai-agents/types';

interface BotTypingIndicatorProps {
  assistantName?: string;
  assistant?: CopilotAssistant | null;
}

// Use centralized animation variants
const dotVariants = CHAT_ANIMATIONS.typingIndicator.dots;
const containerVariants = CHAT_ANIMATIONS.typingIndicator.container;

export const BotTypingIndicator: React.FC<BotTypingIndicatorProps> = ({ 
  assistantName,
  assistant
}) => {
  return (
    <motion.div
      variants={containerVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex items-start gap-3"
    >
      <div className="flex flex-col max-w-[95%] md:max-w-[95%]">
        <motion.div
          whileHover={{ scale: 1.01 }}
          className="px-4 py-2.5 rounded-2xl rounded-tl-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/50 shadow-sm"
        >
          <div className="flex gap-1 items-center h-5">
            <span className="text-xs text-slate-500 dark:text-slate-400 mr-1 font-medium">
              {assistantName || 'Assistant'} is typing
            </span>
            <div className="flex gap-1">
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  custom={i}
                  variants={dotVariants}
                  animate="animate"
                  className="w-1.5 h-1.5 rounded-full bg-blue-400 dark:bg-blue-500"
                />
              ))}
            </div>
          </div>
        </motion.div>
        
        <div className="text-[10px] text-slate-400 dark:text-slate-500 px-1 mt-1 flex items-center">
          <div className="h-4 w-4 mr-1 rounded-full overflow-hidden bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center flex-shrink-0">
            {assistant?.profile_picture_url ? (
              <OptimizedImage 
                src={assistant.profile_picture_url}
                alt={assistant?.name || 'Assistant'}
                className="h-4 w-4 object-cover"
              />
            ) : (
              <span className="text-[6px] text-blue-600 dark:text-blue-400 font-bold">
                {assistantName?.substring(0, 2) || 'AI'}
              </span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default BotTypingIndicator;
